<template>
  <div class="direction-filters-wrapper">
    <button
      class="filter-button all-filters"
      :class="{ 'has-selections': hasAnyFilters }"
      @click="openFiltersModal"
    >
      <img :src="currentFilterIcon" alt="" class="filter-icon" />
    </button>

    <div class="direction-filters">
      <button
        v-for="direction in directions"
        :key="direction.id"
        class="filter-button direction-button"
        :class="{ active: isSelected(direction.id) }"
        @click="toggleDirection(direction.id)"
      >
        <span class="direction-name">{{ direction.name }}</span>
      </button>
    </div>

    <div v-if="isFiltersModalOpen" class="modal-overlay" @click.self="closeFiltersModal">
      <div class="modal-content">
        <div class="modal-header">
          <h3 class="modal-title">Фильтры</h3>
          <button class="modal-close" @click="closeFiltersModal">
            <img :src="closeIcon" alt="Закрыть" class="modal-close-icon" />
          </button>
        </div>

        <div class="modal-section">
          <div class="section-title">Время</div>
          <div class="options-grid">
            <button
              v-for="option in timeOptions"
              :key="option.id"
              class="modal-filter-button"
              :class="{ active: draftTimeRanges.includes(option.id) }"
              @click="toggleDraftTimeRange(option.id)"
            >
              <span class="option-title">{{ option.label }}</span>
              <span class="option-description">{{ option.time }}</span>
            </button>
          </div>
        </div>

        <div class="modal-section">
          <div class="section-title">Длительность</div>
          <div class="duration-grid">
            <button
              v-for="option in durationOptions"
              :key="option"
              class="modal-filter-button duration-button"
              :class="{ active: draftDurations.includes(option) }"
              @click="toggleDraftDuration(option)"
            >
              {{ option }} мин
            </button>
          </div>
        </div>

        <div class="modal-section">
          <div class="section-title">Категории</div>
          <div class="categories-grid">
            <button
              v-for="direction in directions"
              :key="`modal-${direction.id}`"
              class="modal-filter-button category-button"
              :class="{ active: isDraftSelected(direction.id) }"
              @click="toggleDraftDirection(direction.id)"
            >
              {{ direction.name }}
            </button>
          </div>
        </div>

        <div class="modal-actions">
          <button class="modal-action-button reset-button" @click="resetFilters">
            Сбросить
          </button>
          <button class="modal-action-button apply-button" @click="applyFilters">
            Применить
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
/* global defineProps, defineEmits */
import { computed, ref } from 'vue'
import closeIcon from '@/widgets/booking/assets/img/icons/booking-close.svg'
import filterIcon from '@/widgets/booking/assets/img/icons/filter.svg'
import filterWhiteIcon from '@/widgets/booking/assets/img/icons/filter-white.svg'

const props = defineProps({
  directions: {
    type: Array,
    required: true
  },
  modelValue: {
    type: Array,
    required: true
  },
  timeRanges: {
    type: Array,
    default: () => []
  },
  durations: {
    type: Array,
    default: () => []
  }
})

const emit = defineEmits(['update:modelValue', 'update:timeRanges', 'update:durations', 'reset-filters'])

const timeOptions = [
  { id: 'morning', label: 'Утро', time: '08:00 - 12:00' },
  { id: 'day', label: 'День', time: '12:00 - 17:00' },
  { id: 'evening', label: 'Вечер', time: '17:00 - 22:00' }
]

const durationOptions = [30, 60, 90, 120]
const isFiltersModalOpen = ref(false)
const draftTimeRanges = ref([])
const draftDurations = ref([])
const draftDirections = ref([])
const selectedDirections = computed(() => props.modelValue)
const defaultDirections = computed(() => props.directions[0]?.id ? [props.directions[0].id] : [])
const hasAnyFilters = computed(() => {
  return selectedDirections.value.length > 0 || props.timeRanges.length > 0 || props.durations.length > 0
})

const currentFilterIcon = computed(() => {
  return hasAnyFilters.value ? filterWhiteIcon : filterIcon
})

const isSelected = (directionId) => {
  return selectedDirections.value.includes(directionId)
}

const isDraftSelected = (directionId) => {
  return draftDirections.value.includes(directionId)
}

const openFiltersModal = () => {
  draftTimeRanges.value = [...props.timeRanges]
  draftDurations.value = [...props.durations]
  draftDirections.value = [...selectedDirections.value]
  isFiltersModalOpen.value = true
}

const closeFiltersModal = () => {
  isFiltersModalOpen.value = false
}

const resetFilters = () => {
  draftTimeRanges.value = []
  draftDurations.value = []
  draftDirections.value = [...defaultDirections.value]
  emit('reset-filters')
  applyFilters()
}

const applyFilters = () => {
  const hasSameDirections =
    draftDirections.value.length === selectedDirections.value.length
    && draftDirections.value.every((directionId, index) => directionId === selectedDirections.value[index])

  if (!hasSameDirections) {
    emit('update:modelValue', [...draftDirections.value])
  }

  emit('update:timeRanges', [...draftTimeRanges.value])
  emit('update:durations', [...draftDurations.value])
  closeFiltersModal()
}

const toggleDirection = (directionId) => {
  if (selectedDirections.value[0] === directionId) {
    return
  }

  emit('update:modelValue', [directionId])
}

const toggleDraftDirection = (directionId) => {
  if (draftDirections.value[0] === directionId) {
    return
  }

  draftDirections.value = [directionId]
}

const toggleDraftTimeRange = (timeRange) => {
  const index = draftTimeRanges.value.indexOf(timeRange)
  if (index === -1) {
    draftTimeRanges.value = [...draftTimeRanges.value, timeRange]
  } else {
    draftTimeRanges.value = draftTimeRanges.value.filter((value) => value !== timeRange)
  }
}

const toggleDraftDuration = (duration) => {
  const index = draftDurations.value.indexOf(duration)
  if (index === -1) {
    draftDurations.value = [...draftDurations.value, duration]
  } else {
    draftDurations.value = draftDurations.value.filter((value) => value !== duration)
  }
}
</script>

<style scoped lang="scss">
@import '@/widgets/booking/assets/scss/variables.scss';

.direction-filters-wrapper {
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 24px;
}

.direction-filters {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1 1 auto;
  min-width: 0;
  background: white;
  overflow-x: auto;
  overflow-y: hidden;
  padding-bottom: 8px;

  &::-webkit-scrollbar {
    height: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: $border-color;
    border-radius: 2px;
  }
}

.all-filters {
  flex: 0 0 auto;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1500;
  padding: 20px;
}

.modal-content {
  width: min(384px, 100%);
  max-height: min(80vh, 720px);
  overflow-y: auto;
  box-sizing: border-box;
  background: #FFFFFF;
  border-radius: 24px;
  padding: 24px 20px 20px;
}

.modal-header {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
}

.modal-title {
  margin: 0;
  font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 17px;
  font-weight: 700;
  line-height: 20px;
  text-align: center;
  color: #252525;
}

.modal-close {
  position: absolute;
  top: 0;
  right: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #F3F5F8;
  border-radius: 7px;
  flex-shrink: 0;
  border: none;
  padding: 0;
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.8;
  }
}

.modal-close-icon {
  width: 14px;
  height: 14px;
  display: block;
}

.modal-section {
  margin-bottom: 24px;
}

.modal-actions {
  display: flex;
  width: 100%;
  gap: 8px;
  margin-top: 32px;
}

.modal-action-button {
  flex: 1 1 0;
  min-width: 0;
  height: 44px;
  border: none;
  border-radius: 12px;
  font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 16px;
  font-weight: 700;
  line-height: 20px;
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.9;
  }
}

.reset-button {
  background: var(--booking-widget-primary-color-soft, #3C67D61A);
  color: var(--booking-widget-primary-color, #3C67D6);
}

.apply-button {
  background: var(--booking-widget-primary-color, #3C67D6);
  color: #FFFFFF;
}

.section-title {
  margin-bottom: 12px;
  font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 16px;
  font-weight: 700;
  line-height: 20px;
  color: #252525;
}

.options-grid,
.duration-grid,
.categories-grid {
  display: flex;
  flex-wrap: nowrap;
  gap: 8px;
}

.options-grid {
  overflow-x: auto;
}

.duration-grid,
.categories-grid {
  overflow-x: auto;
}

.duration-grid {
  overflow-y: hidden;
  padding-bottom: 8px;
}

.categories-grid {
  flex-wrap: wrap;
  overflow-x: visible;
}

.filter-button {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  background: #F3F5F8;
  border: 2px solid transparent;
  border-radius: 12px;
  font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #757885;
  transition: all 0.2s ease;
  white-space: nowrap;
  flex-shrink: 0;
  height: 32px;

  &:not(:disabled):hover {
    opacity: 0.9;
  }

  &.active {
    background: var(--booking-widget-primary-color, #3C67D6);
    color: white;
    border-color: var(--booking-widget-primary-color, #3C67D6);

    &:hover {
      opacity: 0.9;
    }
  }

  &.all-filters {
    padding: 6px 12px;
    opacity: 1;
    cursor: pointer;

    &.has-selections {
      background: var(--booking-widget-primary-color, #3C67D6);
    }
  }
}

.modal-filter-button {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  padding: 12px 14px;
  background: #F3F5F8;
  border: 2px solid transparent;
  border-radius: 14px;
  font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  color: #333333;
  text-align: left;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: darken(#F3F5F8, 3%);
  }

  &.active {
    background: var(--booking-widget-primary-color, #3C67D6);
    border-color: var(--booking-widget-primary-color, #3C67D6);
    color: #FFFFFF;

    .option-description {
      color: rgba(255, 255, 255, 0.85);
    }
  }
}

.options-grid .modal-filter-button {
  align-items: center;
  text-align: center;
}

.duration-button,
.category-button {
  min-height: 32px;
  height: 32px;
  border-radius: 12px;
  justify-content: center;
  color: #757885;
  font-size: 14px;
  font-weight: 500;
  line-height: 16px;
}

.duration-button {
  flex: 0 0 auto;
  white-space: nowrap;
}

.category-button {
  flex: 0 0 auto;
  width: fit-content;
  white-space: nowrap;
}

.option-title {
  font-size: 15px;
  font-weight: 700;
  line-height: 20px;
}

.option-description {
  font-size: 13px;
  font-weight: 400;
  line-height: 18px;
  color: #757885;
}

.filter-icon {
  width: 20px;
  height: 20px;
  display: block;
}

.direction-name {
  line-height: 1;
}

@media (max-width: 480px) {
  .modal-overlay {
    padding: 12px;
  }

  .modal-content {
    border-radius: 20px;
    padding: 20px 16px 16px;
  }
}
</style>
